import React from 'react';
import { AttemptProgressInfo } from '../types';
import { useTranslation } from '../../../i18n';

interface AttemptProgressProps {
  progress?: AttemptProgressInfo;
  currentTurn?: number;
  maxTurns?: number;
  durationSeconds?: number;
  timeLimitSeconds?: number;
  showLabels?: boolean;
}

export const AttemptProgress: React.FC<AttemptProgressProps> = ({
  progress,
  currentTurn = 0,
  maxTurns = 10,
  durationSeconds = 0,
  timeLimitSeconds = 900,
  showLabels = true,
}) => {
  const { t } = useTranslation();
  const turns = progress ? progress.currentTurn : currentTurn;
  const totalTurns = progress ? progress.maxTurns : maxTurns;
  const turnPct = progress
    ? progress.turnProgressPercent
    : Math.min(100, Math.round((turns / Math.max(1, totalTurns)) * 100));

  const duration = progress ? progress.durationSeconds : durationSeconds;
  const timeLimit = progress ? progress.timeLimitSeconds : timeLimitSeconds;
  const timePct = progress
    ? progress.timeProgressPercent
    : Math.min(100, Math.round((duration / Math.max(1, timeLimit)) * 100));

  const formatDuration = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const s = sec % 60;
    return `${mins}m ${s < 10 ? '0' : ''}${s}s`;
  };

  return (
    <div className="space-y-2 text-sm">
      <div>
        {showLabels && (
          <div className="flex justify-between items-center text-xs text-slate-500 dark:text-slate-400 mb-1">
            <span>{t('attempts.progress.turns', undefined, 'Turns Progress')}</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {turns} / {totalTurns} {t('attempts.progress.turns_unit', undefined, 'turns')} ({turnPct}%)
            </span>
          </div>
        )}
        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${turnPct}%` }}
          />
        </div>
      </div>

      <div>
        {showLabels && (
          <div className="flex justify-between items-center text-xs text-slate-500 dark:text-slate-400 mb-1">
            <span>{t('attempts.progress.elapsed_time', undefined, 'Elapsed Time')}</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {formatDuration(duration)} / {formatDuration(timeLimit)}
            </span>
          </div>
        )}
        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${
              timePct > 85 ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${timePct}%` }}
          />
        </div>
      </div>
    </div>
  );
};
